import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import DocumentService from '../../Services/documents.service';

export const fetchDocumentPageData = createAsyncThunk(
    'documentPage/fetchData',
    async ({ caseId, startDate, endDate, documentType, useRegistrationMonth, isRange, accountantId, includeVatBreakdown, requestKey, token, backgroundRefresh }, { rejectWithValue }) => {
        try {
            const response = await DocumentService.GetDocumentPageData({
                caseId,
                startDate,
                endDate,
                documentType,
                useRegistrationMonth,
                isRange,
                accountantId: accountantId ?? undefined,
                includeVatBreakdown: includeVatBreakdown === true
            }, token);
            if (!response.ok) return rejectWithValue(await response.text());
            const json = await response.json();
            return requestKey != null ? { ...json, requestKey, backgroundRefresh } : { ...json, backgroundRefresh };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const recalculateCreditDebit = createAsyncThunk(
    'documentPage/recalculateCreditDebit',
    async ({ caseId, startDate, endDate, documentType, documentIds, token }, { rejectWithValue }) => {
        try {
            const response = await DocumentService.CalculateCreditDebitSum(
                caseId, startDate, endDate, documentType, token, documentIds
            );
            if (!response.ok) return rejectWithValue(await response.text());
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    documents: [],
    vatAggregates: null,
    creditDebitSum: null,
    missingCards: [],
    isClosedReportingMonth: false,
    loading: false,
    error: null,
    lastFetchKey: null,
    expectedFetchKey: null,
};

const documentPageSlice = createSlice({
    name: 'documentPage',
    initialState,
    reducers: {
        resetDocumentPage: () => initialState,
        setDocuments: (state, action) => {
            state.documents = action.payload;
        },
        setMonthClosed: (state, action) => {
            state.isClosedReportingMonth = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentPageData.pending, (state, action) => {
                const backgroundRefresh = action.meta?.arg?.backgroundRefresh;
                const hasExistingData = (state.documents?.length ?? 0) > 0;
                if (!(backgroundRefresh && hasExistingData)) state.loading = true;
                state.error = null;
                const key = action.meta?.arg?.requestKey;
                if (key != null) state.expectedFetchKey = key;
            })
            .addCase(fetchDocumentPageData.fulfilled, (state, action) => {
                const r = action.payload;
                const requestKey = r.requestKey;
                if (requestKey != null && state.expectedFetchKey != null && requestKey !== state.expectedFetchKey) {
                    state.loading = false;
                    return;
                }
                state.documents = Array.isArray(r.documents) ? r.documents : (r.documents?.result ?? []);
                // detailedVat: Incomes => { income, expense, equipment }; Expenses => { income, expense, equipment }
                const incomeVatTop = r.incomeVat ?? r.IncomeVat;
                const detailedVat = r.detailedVat ?? r.DetailedVat;
                if (detailedVat) {
                    state.vatAggregates = {
                        income: detailedVat.income ?? detailedVat.Income ?? incomeVatTop ?? null,
                        expense: detailedVat.expense ?? detailedVat.Expense ?? null,
                        equipment: detailedVat.equipment ?? detailedVat.Equipment ?? null
                    };
                } else {
                    state.vatAggregates = {
                        income: incomeVatTop ?? null,
                        expense: r.expenseVat ?? r.ExpenseVat ?? null,
                        equipment: r.equipmentVat ?? r.EquipmentVat ?? null
                    };
                }
                state.creditDebitSum = { creditSum: r.creditSum, debitSum: r.debitSum, difference: r.difference };
                state.missingCards = r.missingCards ?? [];
                state.isClosedReportingMonth = r.isClosedReportingMonth ?? r.IsClosedReportingMonth ?? false;
                state.loading = false;
            })
            .addCase(fetchDocumentPageData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(recalculateCreditDebit.fulfilled, (state, action) => {
                const p = action.payload;
                state.creditDebitSum = { creditSum: p.creditSum, debitSum: p.debitSum, difference: p.difference };
                state.missingCards = p.missingCards ?? [];
            });
    },
});

export const { resetDocumentPage, setDocuments, setMonthClosed } = documentPageSlice.actions;
export default documentPageSlice.reducer;
